import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import PageHero from "@/components/PageHero";
import { Phone, Mail, MapPin, Send, Loader2, CheckCircle2, Instagram, Facebook, Youtube, ExternalLink, CalendarX, Check, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { submitQuote, publicApi, publicCatalogApi, brochureApi } from "@/lib/api";
import BrochureModal from "@/components/BrochureModal";
import { trackEvent, cityGroupFromValue, budgetRange, getLeadContext, trackGenerateLeadOnce } from "@/lib/analytics";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { capitalizeName, lowerEmail } from "@/lib/text";
import { useSeo } from "@/lib/seo";

const MA_SOCIALS = [
  { name: "Instagram", Icon: Instagram, url: "https://www.instagram.com/myanimationdj/" },
  { name: "Facebook", Icon: Facebook, url: "https://www.facebook.com/p/Dj-Paps-MY-Animation-61574555792594/" },
  { name: "YouTube", Icon: Youtube, url: "https://www.youtube.com/@yannickmulier2998" },
];
const MA_REFERENCES = [
  { name: "Mariages.net", url: "https://www.mariages.net/musique-mariage/my-animation--e386697" },
  { name: "Bark", url: "https://www.bark.com/fr/fr/b/my-animation-/N03P4q/" },
];

const EVENT_TYPES = [
  "Mariage",
  "Anniversaire",
  "Soirée privée",
  "Événement d'entreprise",
  "Séminaire / Gala",
  "Mairie / Collectivité",
  "Association",
  "Résidence senior / EHPAD",
  "Photobooth seul",
  "Blind test / Karaoké",
  "Autre",
];

// Map user-facing event type → catalog category. Empty = no packages to preview.
const TYPE_TO_CATEGORY = {
  "Mariage": "mariage",
  "Anniversaire": "anniversaire",
  "Soirée privée": "anniversaire",
  "Événement d'entreprise": "entreprise",
  "Séminaire / Gala": "entreprise",
  "Mairie / Collectivité": "mairie",
  "Association": "association",
  "Résidence senior / EHPAD": "senior",
  "Photobooth seul": "photobooth",
  "Blind test / Karaoké": "blindtest",
};

const PRESTATIONS = [
  "DJ",
  "Photobooth",
  "Blind test",
  "Karaoké",
  "Chanteur live",
  "Saxophoniste",
  "Mise en lumière",
  "Sonorisation",
  "Micro cérémonie/discours",
  "Étincelles froides",
  "Autre",
];

export default function Contact() {
  useSeo({
    title: "Demande de devis DJ événementiel | My Animation",
    description:
      "Demandez votre devis personnalisé pour une prestation DJ, photobooth, blind test, karaoké ou mise en lumière avec My Animation. Devis gratuit et sans engagement.",
    path: "/contact",
    keywords: "devis DJ événementiel Hauts-de-France, contact My Animation, devis photobooth, devis mariage Lille",
  });

  const [form, setForm] = useState({
    prenom: "",
    nom: "",
    email: "",
    telephone: "",
    adresse: "",
    code_postal: "",
    ville: "",
    type_evenement: "",
    date_evenement: "",
    lieu: "",
    nb_invites: "",
    horaires: "",
    prestations: [],
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [unavailable, setUnavailable] = useState([]);
  const [suggestedPacks, setSuggestedPacks] = useState([]);
  const [selectedForfait, setSelectedForfait] = useState(null); // { id, name, price_label }
  const [loadingPacks, setLoadingPacks] = useState(false);
  const [brochureOpen, setBrochureOpen] = useState(false);
  // Flag anti-doublon pour form_start (déclenché UNE SEULE FOIS par montage)
  const [formStarted, setFormStarted] = useState(false);

  // Contexte de provenance (page locale → /contact) — lu une fois au montage,
  // conservé pour tous les événements liés à cette session de formulaire.
  // Non-personnel : service_type, page_type, city (slug), source_page.
  const leadCtx = useMemo(() => getLeadContext() || {}, []);
  const trackCtxBase = useMemo(() => ({
    service_type: leadCtx.service_type || "",
    page_type: "contact",
    city: leadCtx.city || "",
    source_page: leadCtx.source_page || "/contact",
  }), [leadCtx]);

  useEffect(() => {
    publicApi.unavailableDates()
      .then((d) => setUnavailable(Array.isArray(d?.dates) ? d.dates : []))
      .catch(() => setUnavailable([]));
  }, []);

  // Auto-scroll to the quote form section on mount so visitors don't have to
  // scroll manually after clicking "Demander un devis" on other pages.
  // Skipped when the URL has an explicit hash (deep-link) or when the visitor
  // has scrolled ~100px already (means they came here from a link that just
  // clicked here as a next step).
  useEffect(() => {
    if (window.location.hash) return;
    const t = setTimeout(() => {
      const el = document.getElementById("devis-form");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 500);
    return () => clearTimeout(t);
  }, []);

  // Pre-fill the form with the client's saved profile when logged in.
  // This avoids re-typing prenom/nom/email/phone/adresse/CP/ville.
  const { user } = useAuth();
  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      prenom: f.prenom || user.prenom || "",
      nom: f.nom || user.nom || "",
      email: f.email || lowerEmail(user.email || ""),
      telephone: f.telephone || user.phone || "",
      adresse: f.adresse || user.adresse || "",
      code_postal: f.code_postal || user.code_postal || "",
      ville: f.ville || user.ville || "",
      type_evenement: f.type_evenement || user.type_evenement || "",
    }));
  }, [user]);

  // When the user picks a type of event, fetch the matching catalog packages
  useEffect(() => {
    const cat = TYPE_TO_CATEGORY[form.type_evenement];
    if (!cat) {
      setSuggestedPacks([]);
      setSelectedForfait(null);
      return;
    }
    setLoadingPacks(true);
    publicCatalogApi.byCategory(cat, false)
      .then((d) => setSuggestedPacks(d?.packages || []))
      .catch(() => setSuggestedPacks([]))
      .finally(() => setLoadingPacks(false));
    setSelectedForfait(null); // reset selection when category changes
  }, [form.type_evenement]);

  const unavailableSet = useMemo(() => new Set(unavailable), [unavailable]);
  const dateBlocked = form.date_evenement && unavailableSet.has(form.date_evenement);

  const update = (k, v) => {
    if (!formStarted) {
      setFormStarted(true);
      trackEvent("form_start", trackCtxBase);
    }
    setForm((f) => ({ ...f, [k]: v }));
  };
  const toggleP = (p) => {
    if (!formStarted) {
      setFormStarted(true);
      trackEvent("form_start", trackCtxBase);
    }
    setForm((f) => ({
      ...f,
      prestations: f.prestations.includes(p)
        ? f.prestations.filter((x) => x !== p)
        : [...f.prestations, p],
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.prenom || !form.nom || !form.email || !form.telephone || !form.type_evenement || !form.adresse || !form.code_postal || !form.ville) {
      toast.error("Merci de compléter les champs obligatoires (nom, prénom, adresse postale complète).");
      return;
    }
    if (form.date_evenement && unavailableSet.has(form.date_evenement)) {
      toast.error("Cette date est déjà réservée. Merci d'en choisir une autre.");
      return;
    }
    setLoading(true);
    // form_submit — déclenché avant l'appel API (une tentative d'envoi,
    // qu'elle réussisse ou non côté serveur).
    const serviceType = (TYPE_TO_CATEGORY[form.type_evenement] || leadCtx.service_type || "autre").toLowerCase();
    trackEvent("form_submit", { ...trackCtxBase, service_type: serviceType });
    try {
      // Build final payload: append selected forfait to message + prestations for clarity in emails/admin.
      let finalMessage = form.message || "";
      let finalPrestations = [...(form.prestations || [])];
      if (selectedForfait) {
        const line = `Forfait souhaité : ${selectedForfait.name} — ${selectedForfait.price_label || (selectedForfait.price_ttc + " €")}`;
        finalMessage = finalMessage ? `${line}\n\n${finalMessage}` : line;
        if (!finalPrestations.includes(selectedForfait.name)) finalPrestations.push(selectedForfait.name);
      }
      const payload = {
        ...form,
        nom: `${form.prenom} ${form.nom}`.trim(),
        message: finalMessage,
        prestations: finalPrestations,
        forfait_id: selectedForfait?.id || "",
        forfait_nom: selectedForfait?.name || "",
      };
      const response = await submitQuote(payload);
      setDone(true);
      toast.success("Demande envoyée ! Nous revenons vers vous rapidement.");
      // generate_lead — déclenché UNIQUEMENT après réponse serveur positive,
      // dédoublonné par quote_id pour éviter double-fire en cas de refresh/back.
      // Aucune donnée personnelle transmise : catégories agrégées uniquement.
      try {
        trackGenerateLeadOnce(
          {
            ...trackCtxBase,
            service_type: serviceType,
            city_group: cityGroupFromValue(form.ville || form.code_postal),
            budget_range: budgetRange(selectedForfait?.price_ttc || 0),
            event_category: "quote_form",
          },
          { dedupKey: response?.id || `quote_${Date.now()}` }
        );
      } catch (_) { /* analytics ne doit jamais casser l'UX */ }
      // Scroll back up to the success message so the user sees it immediately.
      setTimeout(() => {
        const el = document.querySelector('[data-testid="quote-success"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        else window.scrollTo({ top: 0, behavior: "smooth" });
      }, 120);
      // Fire-and-forget brochure email — bonus conversion tool
      brochureApi.sendByEmail({
        email: form.email,
        name: form.prenom || form.nom || "",
        consent: true,
      }).catch(() => { /* fail-soft */ });
      setForm({
        prenom: "", nom: "", email: "", telephone: "", adresse: "", code_postal: "", ville: "",
        type_evenement: "", date_evenement: "", lieu: "", nb_invites: "", horaires: "", prestations: [], message: "",
      });
      setSelectedForfait(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur lors de l'envoi. Réessayez ou appelez-nous.";
      toast.error(typeof msg === "string" ? msg : "Erreur lors de l'envoi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Contact / Devis"
        title={<>Parlons de votre <span className="italic gold-text">événement.</span></>}
        subtitle="Devis gratuit, personnalisé et sans engagement. Nous intervenons dans les Hauts-de-France, le Nord, le Pas-de-Calais et la Belgique — de Lille à Tourcoing, Roubaix, Armentières, Dunkerque, Lens et Arras."
        img="https://images.pexels.com/photos/9665186/pexels-photo-9665186.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
      />

      <section className="py-24 md:py-32 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Coordonnées */}
          <aside className="lg:col-span-4">
            <div className="card-luxe p-8 md:p-10 sticky top-28">
              <span className="eyebrow">Coordonnées</span>
              <h2 className="font-serif-luxe text-3xl md:text-4xl mt-4">
                Une question ?<br />
                <span className="italic gold-text">Appelez-nous.</span>
              </h2>
              <div className="mt-8 space-y-5">
                <a href="tel:0769224956" data-testid="contact-phone-link" onClick={() => trackEvent("click_phone", trackCtxBase)} className="flex items-start gap-4 group">
                  <Phone size={20} strokeWidth={1.2} className="text-[#DCB457] mt-1" />
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-white/45">Téléphone</div>
                    <div className="font-serif-luxe text-2xl group-hover:text-[#DCB457] transition">07 69 22 49 56</div>
                  </div>
                </a>
                <a href="mailto:my-animation@outlook.fr" data-testid="contact-email-link" onClick={() => trackEvent("click_email", trackCtxBase)} className="flex items-start gap-4 group">
                  <Mail size={20} strokeWidth={1.2} className="text-[#DCB457] mt-1" />
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-white/45">Email</div>
                    <div className="text-base group-hover:text-[#DCB457] transition break-all">my-animation@outlook.fr</div>
                  </div>
                </a>
                <div className="flex items-start gap-4">
                  <MapPin size={20} strokeWidth={1.2} className="text-[#DCB457] mt-1" />
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-white/45">Zone d'intervention</div>
                    <div className="text-sm text-white/80 mt-1">Hauts-de-France · Nord · Pas-de-Calais · Belgique</div>
                  </div>
                </div>
              </div>
              <div className="mt-10 pt-8 border-t border-[#DCB457]/15">
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#DCB457] mb-3">Brochure 2026</p>
                <p className="text-sm text-white/55 leading-relaxed mb-4">
                  Recevez notre brochure premium avec l'ensemble des formules et tarifs.
                </p>
                <button
                  type="button"
                  onClick={() => setBrochureOpen(true)}
                  data-testid="contact-brochure-btn"
                  className="btn-ghost-gold w-full inline-flex items-center justify-center gap-2"
                >
                  <BookOpen size={14} strokeWidth={1.4} /> Recevoir la brochure
                </button>
              </div>

              <div className="mt-10 pt-8 border-t border-[#DCB457]/15">
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#DCB457] mb-3">Espace client</p>
                <p className="text-sm text-white/55 leading-relaxed mb-4">
                  Vous êtes déjà client ? Accédez à votre espace dédié.
                </p>
                <a
                  href="/espace-client"
                  data-testid="contact-espace-client-btn"
                  className="btn-ghost-gold w-full"
                >
                  Accéder à mon espace
                </a>
              </div>

              <div className="mt-10 pt-8 border-t border-[#DCB457]/15">
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#DCB457] mb-3">Suivez My Animation</p>
                <div className="flex items-center gap-3">
                  {MA_SOCIALS.map(({ name, Icon, url }) => (
                    <a
                      key={name}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${name} My Animation`}
                      data-testid={`contact-social-${name.toLowerCase()}`}
                      className="w-11 h-11 border border-[#DCB457]/30 flex items-center justify-center hover:bg-[#DCB457]/10 hover:border-[#DCB457] transition"
                    >
                      <Icon size={17} strokeWidth={1.2} className="text-[#DCB457]" />
                    </a>
                  ))}
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {MA_REFERENCES.map((r) => (
                    <a
                      key={r.name}
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`contact-ref-${r.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-white/55 border border-[#DCB457]/20 px-3 py-1.5 hover:text-[#DCB457] hover:border-[#DCB457]/50 transition"
                    >
                      {r.name} <ExternalLink size={10} strokeWidth={1.4} />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-8">
            {done ? (
              <div className="card-luxe p-12 md:p-16 text-center scroll-mt-28" data-testid="quote-success">
                <CheckCircle2 size={48} strokeWidth={1} className="text-[#DCB457] mx-auto mb-6" />
                <h2 className="font-serif-luxe text-4xl md:text-5xl">
                  Merci !<br />
                  <span className="italic gold-text">Demande bien reçue.</span>
                </h2>
                <p className="text-white/65 mt-6 max-w-md mx-auto">
                  Nous revenons vers vous rapidement avec une proposition personnalisée.
                  Pour toute urgence, appelez le 07 69 22 49 56.
                </p>
                <button
                  type="button"
                  data-testid="quote-success-reset"
                  onClick={() => setDone(false)}
                  className="btn-ghost-gold mt-10"
                >
                  Nouvelle demande
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="quote-form" id="devis-form" className="card-luxe p-8 md:p-12 space-y-6 scroll-mt-28">
                <div className="mb-2">
                  <span className="eyebrow">Devis personnalisé</span>
                  <h2 className="font-serif-luxe text-3xl md:text-4xl mt-3">
                    Décrivez votre <span className="italic gold-text">projet.</span>
                  </h2>
                  <p className="text-white/65 text-sm mt-3 leading-relaxed">
                    Décrivez votre projet en quelques informations. Nous vous répondrons
                    rapidement avec une proposition personnalisée.
                  </p>
                  <p className="text-[#DCB457] text-[11px] uppercase tracking-[0.2em] mt-3">
                    Devis gratuit, personnalisé et sans engagement.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Prénom *">
                    <input className="input-luxe" required value={form.prenom} onChange={(e) => update("prenom", capitalizeName(e.target.value))} data-testid="quote-input-prenom" placeholder="Marie" />
                  </Field>
                  <Field label="Nom *">
                    <input className="input-luxe" required value={form.nom} onChange={(e) => update("nom", capitalizeName(e.target.value))} data-testid="quote-input-nom" placeholder="Dupont" />
                  </Field>
                  <Field label="Téléphone *">
                    <input className="input-luxe" required value={form.telephone} onChange={(e) => update("telephone", e.target.value)} data-testid="quote-input-telephone" placeholder="06 12 34 56 78" />
                  </Field>
                  <Field label="E-mail *">
                    <input type="email" className="input-luxe" required value={form.email} onChange={(e) => update("email", lowerEmail(e.target.value))} data-testid="quote-input-email" placeholder="email@exemple.fr" />
                  </Field>
                  <Field label="Adresse postale *">
                    <AddressAutocomplete
                      value={form.adresse}
                      onChange={(v) => update("adresse", v)}
                      onSelect={(s) => {
                        setForm((f) => ({
                          ...f,
                          adresse: s.line1 || s.street || s.label,
                          code_postal: s.postcode || f.code_postal,
                          ville: s.city || f.ville,
                        }));
                      }}
                      required
                      testId="quote-input-adresse"
                    />
                  </Field>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-1">
                      <Field label="Code postal *">
                        <input className="input-luxe" required value={form.code_postal} onChange={(e) => update("code_postal", e.target.value)} data-testid="quote-input-cp" placeholder="59000" maxLength={10} />
                      </Field>
                    </div>
                    <div className="col-span-2">
                      <Field label="Ville *">
                        <input className="input-luxe" required value={form.ville} onChange={(e) => update("ville", capitalizeName(e.target.value))} data-testid="quote-input-ville" placeholder="Lille" />
                      </Field>
                    </div>
                  </div>
                  <Field label="Type d'événement *">
                    <select className="input-luxe" required value={form.type_evenement} onChange={(e) => update("type_evenement", e.target.value)} data-testid="quote-select-type">
                      <option value="">— Choisir —</option>
                      {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Field>
                </div>

                {/* Forfait picker — dynamic, driven by catalogue produits */}
                {form.type_evenement && (loadingPacks || suggestedPacks.length > 0) && (
                  <div className="border border-[#DCB457]/25 bg-[#0a0a0a] p-5 md:p-6" data-testid="quote-forfait-picker">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                      <p className="eyebrow">Nos forfaits {form.type_evenement.toLowerCase()}</p>
                      {selectedForfait && (
                        <button
                          type="button"
                          onClick={() => setSelectedForfait(null)}
                          data-testid="quote-forfait-clear"
                          className="text-[11px] uppercase tracking-[0.18em] text-white/50 hover:text-[#DCB457]"
                        >
                          Effacer la sélection
                        </button>
                      )}
                    </div>
                    <p className="text-white/50 text-xs mb-4">
                      Sélectionnez le forfait qui vous intéresse (facultatif). Il sera automatiquement joint à votre demande.
                    </p>
                    {loadingPacks ? (
                      <p className="text-white/45 text-sm italic">Chargement des forfaits…</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="quote-forfait-grid">
                        {suggestedPacks.map((p) => {
                          const isSelected = selectedForfait?.id === p.id;
                          const price = p.price_label && p.price_label.trim() ? p.price_label : `${Math.round(p.price_ttc)} €`;
                          return (
                            <button
                              type="button"
                              key={p.id}
                              onClick={() => setSelectedForfait(isSelected ? null : p)}
                              data-testid={`quote-forfait-${p.id}`}
                              className={`text-left p-4 border transition relative ${isSelected ? "border-[#DCB457] bg-[#DCB457]/8" : "border-[#DCB457]/15 hover:border-[#DCB457]/45"}`}
                            >
                              {isSelected && (
                                <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#DCB457] text-black flex items-center justify-center">
                                  <Check size={12} strokeWidth={2} />
                                </span>
                              )}
                              {p.is_featured && !isSelected && (
                                <span className="text-[9px] uppercase tracking-[0.22em] text-[#DCB457] mb-1.5 inline-block">
                                  Le plus choisi
                                </span>
                              )}
                              <p className="font-serif-luxe text-base leading-tight">{p.name}</p>
                              {p.subtitle && (
                                <p className="text-white/50 text-[11px] mt-1 leading-snug line-clamp-2">{p.subtitle}</p>
                              )}
                              <p className="font-serif-luxe text-lg gold-text mt-2">{price}</p>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <Link
                      to={
                        {
                          mariage: "/mariage",
                          anniversaire: "/anniversaire",
                          bapteme: "/baptemes-communions",
                          senior: "/seniors",
                          association: "/associations",
                          photobooth: "/photobooth",
                          blindtest: "/blind-test-karaoke",
                          entreprise: "/entreprises",
                          mairie: "/collectivites",
                        }[TYPE_TO_CATEGORY[form.type_evenement]] || "/prestations"
                      }
                      className="text-[11px] uppercase tracking-[0.18em] text-[#DCB457] hover:underline mt-4 inline-block"
                      data-testid="quote-forfait-see-details"
                    >
                      Voir tous les détails →
                    </Link>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Date de l'événement *">
                    <input
                      type="date"
                      required
                      className={`input-luxe ${dateBlocked ? "border-red-400 focus:border-red-400" : ""}`}
                      value={form.date_evenement}
                      onChange={(e) => update("date_evenement", e.target.value)}
                      data-testid="quote-input-date"
                      min={new Date().toISOString().slice(0, 10)}
                    />
                    {dateBlocked && (
                      <p data-testid="quote-date-blocked" className="mt-2 inline-flex items-center gap-2 text-[12px] text-red-300">
                        <CalendarX size={13} strokeWidth={1.4} />
                        Désolé, cette date est déjà réservée. Merci d'en choisir une autre.
                      </p>
                    )}
                    {!dateBlocked && unavailable.length > 0 && (
                      <p className="mt-2 text-[11px] text-white/40">
                        {unavailable.length} date{unavailable.length > 1 ? "s" : ""} déjà réservée{unavailable.length > 1 ? "s" : ""} — vous serez prévenu si vous choisissez l'une d'elles.
                      </p>
                    )}
                  </Field>
                  <Field label="Lieu de l'événement *">
                    <AddressAutocomplete
                      value={form.lieu}
                      onChange={(v) => update("lieu", v)}
                      onSelect={(s) => {
                        setForm((f) => ({ ...f, lieu: s.label || s.street }));
                      }}
                      required
                      placeholder="Salle, château, restaurant…"
                      testId="quote-input-lieu"
                    />
                  </Field>
                  <Field label="Nombre d'invités *">
                    <input
                      className="input-luxe"
                      type="number"
                      min="1"
                      max="2000"
                      required
                      value={form.nb_invites}
                      onChange={(e) => update("nb_invites", e.target.value)}
                      data-testid="quote-input-invites"
                      placeholder="ex. 120"
                    />
                  </Field>
                  <Field label="Horaires souhaités *">
                    <select
                      className="input-luxe"
                      required
                      value={form.horaires}
                      onChange={(e) => update("horaires", e.target.value)}
                      data-testid="quote-input-horaires"
                    >
                      <option value="">Choisir un créneau…</option>
                      <option value="14h - 19h">Après-midi (14h - 19h) — vin d'honneur / anniversaire jour</option>
                      <option value="18h - 00h">Soirée courte (18h - 00h) — 6h d'animation</option>
                      <option value="19h - 02h">Soirée standard (19h - 02h) — 7h</option>
                      <option value="20h - 03h">Soirée mariage (20h - 03h) — 7h</option>
                      <option value="20h - 04h">Soirée mariage extended (20h - 04h) — 8h</option>
                      <option value="19h - 04h">Soirée complète (19h - 04h) — 9h</option>
                      <option value="21h - 05h">Nuit blanche (21h - 05h) — 8h</option>
                      <option value="autre">Autre créneau (précisez en message)</option>
                    </select>
                  </Field>
                </div>

                <div>
                  <label className="block text-[11px] uppercase tracking-[0.2em] text-white/60 mb-3">
                    Prestations souhaitées
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {PRESTATIONS.map((p) => {
                      const active = form.prestations.includes(p);
                      const slug = p
                        .normalize("NFD")
                        .replace(/[\u0300-\u036f]/g, "")
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")
                        .replace(/^-|-$/g, "");
                      const id = `pres-${slug}`;
                      return (
                        <label
                          key={p}
                          htmlFor={id}
                          data-testid={`quote-pres-${slug}`}
                          className={`cursor-pointer flex items-center gap-3 text-[12px] uppercase tracking-[0.14em] px-4 py-2.5 border transition ${
                            active
                              ? "bg-[#DCB457] text-black border-[#DCB457]"
                              : "bg-transparent border-[#DCB457]/30 text-white/70 hover:border-[#DCB457]"
                          }`}
                        >
                          <input
                            id={id}
                            type="checkbox"
                            checked={active}
                            onChange={() => toggleP(p)}
                            className="sr-only"
                          />
                          <span className={`w-3.5 h-3.5 border ${active ? "border-black bg-black" : "border-[#DCB457]/60"}`}>
                            {active && (
                              <svg viewBox="0 0 12 12" className="w-full h-full text-[#DCB457]">
                                <path d="M2 6l3 3 5-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </span>
                          {p}
                        </label>
                      );
                    })}
                  </div>
                </div>

                <Field label="Message libre">
                  <textarea
                    rows={5}
                    className="input-luxe resize-none"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    data-testid="quote-input-message"
                    placeholder="Précisez vos envies, l'ambiance souhaitée, des moments-clés…"
                  />
                </Field>

                <button
                  type="submit"
                  disabled={loading || dateBlocked}
                  data-testid="quote-submit-btn"
                  className="btn-gold w-full md:w-auto disabled:opacity-60"
                >
                  {loading ? (
                    <><Loader2 size={16} className="animate-spin" /> Envoi en cours…</>
                  ) : (
                    <>Envoyer ma demande <Send size={14} strokeWidth={1.4} /></>
                  )}
                </button>

                <p className="text-xs text-white/40 leading-relaxed pt-2">
                  Vos données sont stockées de manière sécurisée et utilisées uniquement
                  pour traiter votre demande de devis. Aucune communication marketing.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
      <BrochureModal open={brochureOpen} onClose={() => setBrochureOpen(false)} />
    </>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.2em] text-white/60 mb-2">{label}</span>
      {children}
    </label>
  );
}
